import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import { Plus, X, Check, Users, GraduationCap, Eye, EyeOff, Edit2, KeyRound, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLES = [
  { key: 'guru', label: 'Guru / Ustaz', icon: GraduationCap },
  { key: 'santri', label: 'Santri', icon: Users },
];

const KELAS_OPTIONS = [
  { value: '', label: '-- Pilih Kelas --' },
  { value: 'D', label: "D — I'dad" },
  { value: 'C', label: 'C — Uula' },
  { value: 'B', label: 'B — Wustha' },
  { value: 'A', label: 'A — Ulya' },
  { value: 'M', label: 'M — Mahir/Ali' },
];

const JENIS_KELAMIN_OPTIONS = [
  { value: '', label: '-- Pilih --' },
  { value: 'L', label: 'Laki-laki' },
  { value: 'P', label: 'Perempuan' },
];

const emptyForm = {
  username: '', name: '', password: '', whatsapp: '',
  bidang: '', halaqah_id: '', nis: '',
  kelas: '', tahun_masuk: '', jenis_kelamin: '',
};

function RoleFields({ f, onChange, currentRole, halaqah }) {
  const set = (field) => (e) => onChange(field, e.target.value);

  if (currentRole === 'guru') {
    return (
      <>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Bidang / Spesialisasi</label>
          <input
            data-testid="field-bidang"
            value={f.bidang}
            onChange={set('bidang')}
            placeholder="Tajwid, Tahfiz, dll."
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Jenis Kelamin</label>
          <select
            data-testid="field-jenis-kelamin"
            value={f.jenis_kelamin}
            onChange={set('jenis_kelamin')}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          >
            {JENIS_KELAMIN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </>
    );
  }

  if (currentRole === 'santri') {
    return (
      <>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">NIS *</label>
            <input
              data-testid="field-nis"
              value={f.nis}
              onChange={set('nis')}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Halaqah</label>
            <select
              data-testid="field-halaqah"
              value={f.halaqah_id}
              onChange={set('halaqah_id')}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="">-- Pilih --</option>
              {halaqah.map(h => <option key={h.id} value={h.id}>{h.nama}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Kelas / Grade</label>
            <select
              data-testid="field-kelas"
              value={f.kelas}
              onChange={set('kelas')}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              {KELAS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tahun Masuk</label>
            <input
              data-testid="field-tahun-masuk"
              type="number"
              min="2000"
              max="2099"
              value={f.tahun_masuk}
              onChange={set('tahun_masuk')}
              placeholder="2024"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Jenis Kelamin</label>
          <select
            data-testid="field-jenis-kelamin"
            value={f.jenis_kelamin}
            onChange={set('jenis_kelamin')}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          >
            {JENIS_KELAMIN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </>
    );
  }

  return null;
}

export default function ManajemenAkun() {
  const [users, setUsers] = useState([]);
  const [halaqah, setHalaqah] = useState([]);
  const [santriList, setSantriList] = useState([]);
  const [guruList, setGuruList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [role, setRole] = useState('guru');
  const [showForm, setShowForm] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [showEditPass, setShowEditPass] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Search by name
  const [searchName, setSearchName] = useState('');

  // Lihat Password
  const [viewPassUser, setViewPassUser] = useState(null);
  const [viewPassData, setViewPassData] = useState('');
  const [viewPassLoading, setViewPassLoading] = useState(false);
  const [showViewPassText, setShowViewPassText] = useState(false);

  // Reset Password
  const [resetPassUser, setResetPassUser] = useState(null);
  const [resetPassInput, setResetPassInput] = useState('');
  const [resetPassLoading, setResetPassLoading] = useState(false);
  const [showResetPassText, setShowResetPassText] = useState(false);

  const handleViewPassword = async (u) => {
    setViewPassUser(u);
    setViewPassData('');
    setShowViewPassText(false);
    setViewPassLoading(true);
    try {
      const r = await axios.get(`${API}/users/${u.id}/password`);
      setViewPassData(r.data.password);
    } catch (err) {
      setViewPassData(null);
      toast.error(err.response?.data?.detail || 'Password tidak tersedia');
    } finally { setViewPassLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPassInput.trim()) { toast.error('Password baru tidak boleh kosong'); return; }
    setResetPassLoading(true);
    try {
      await axios.put(`${API}/users/${resetPassUser.id}`, { password: resetPassInput });
      toast.success(`Password ${resetPassUser.name} berhasil direset`);
      setResetPassUser(null);
      setResetPassInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mereset password');
    } finally { setResetPassLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [u, h, s, g] = await Promise.all([
      axios.get(`${API}/users`).then(r => r.data).catch(() => []),
      axios.get(`${API}/halaqah`).then(r => r.data).catch(() => []),
      axios.get(`${API}/santri`).then(r => r.data).catch(() => []),
      axios.get(`${API}/guru`).then(r => r.data).catch(() => []),
    ]);
    setUsers(u); setHalaqah(h); setSantriList(s); setGuruList(g);
  };

  const setFormField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setEditFormField = (field, value) => setEditForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.name) {
      toast.error('Username, nama, dan password wajib diisi'); return;
    }
    setLoading(true);
    try {
      const userData = {
        username: form.username, name: form.name, role,
        password: form.password, whatsapp: form.whatsapp,
      };
      const { data: newUser } = await axios.post(`${API}/users`, userData);

      if (role === 'guru') {
        await axios.post(`${API}/guru`, {
          name: form.name, user_id: newUser.id, whatsapp: form.whatsapp,
          bidang: form.bidang, kelas: form.kelas,
          tahun_masuk: form.tahun_masuk, jenis_kelamin: form.jenis_kelamin,
        });
      } else if (role === 'santri' && form.nis) {
        const santriDoc = {
          nis: form.nis, name: form.name, halaqah_id: form.halaqah_id,
          user_id: newUser.id, kelas: form.kelas,
          tahun_masuk: form.tahun_masuk, jenis_kelamin: form.jenis_kelamin,
        };
        const { data: newSantri } = await axios.post(`${API}/santri`, santriDoc);
        await axios.put(`${API}/users/${newUser.id}`, { santri_id: newSantri.id });
      }

      toast.success(`Akun ${form.name} (${role}) berhasil dibuat!`);
      setShowForm(false); setForm(emptyForm);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat akun');
    } finally { setLoading(false); }
  };

  const openEdit = (u) => {
    if (u.role === 'santri') {
      const s = santriList.find(x => x.id === u.santri_id) || santriList.find(x => x.user_id === u.id) || {};
      setEditForm({
        username: u.username, name: u.name, password: '',
        whatsapp: u.whatsapp || '', bidang: '',
        halaqah_id: s.halaqah_id || '', nis: s.nis || '',
        kelas: s.kelas || '', tahun_masuk: s.tahun_masuk || '',
        jenis_kelamin: s.jenis_kelamin || '',
      });
    } else if (u.role === 'guru') {
      const g = guruList.find(x => x.user_id === u.id) || {};
      setEditForm({
        username: u.username, name: u.name, password: '',
        whatsapp: u.whatsapp || '', bidang: g.bidang || '',
        halaqah_id: '', nis: '',
        kelas: g.kelas || '', tahun_masuk: g.tahun_masuk || '',
        jenis_kelamin: g.jenis_kelamin || '',
      });
    } else {
      setEditForm({ ...emptyForm, username: u.username, name: u.name, whatsapp: u.whatsapp || '' });
    }
    setShowEditPass(false);
    setEditUser(u);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.name) { toast.error('Nama wajib diisi'); return; }
    const newUsername = editForm.username.trim().toLowerCase();
    if (!newUsername) { toast.error('Username wajib diisi'); return; }
    // Client-side duplicate check (backend double-checks)
    const duplicate = users.find(u => u.username === newUsername && u.id !== editUser.id);
    if (duplicate) { toast.error('Username sudah digunakan akun lain'); return; }
    setEditLoading(true);
    try {
      const userUpdate = { name: editForm.name, whatsapp: editForm.whatsapp, username: newUsername };
      if (editForm.password) userUpdate.password = editForm.password;
      await axios.put(`${API}/users/${editUser.id}`, userUpdate);

      if (editUser.role === 'santri' && editUser.santri_id) {
        await axios.put(`${API}/santri/${editUser.santri_id}`, {
          nis: editForm.nis || '—',
          name: editForm.name,
          halaqah_id: editForm.halaqah_id,
          kelas: editForm.kelas,
          tahun_masuk: editForm.tahun_masuk,
          jenis_kelamin: editForm.jenis_kelamin,
          user_id: editUser.id,
        });
      } else if (editUser.role === 'guru') {
        const guruDoc = guruList.find(g => g.user_id === editUser.id);
        if (guruDoc) {
          await axios.put(`${API}/guru/${guruDoc.id}`, {
            name: editForm.name,
            whatsapp: editForm.whatsapp,
            bidang: editForm.bidang,
            kelas: editForm.kelas,
            tahun_masuk: editForm.tahun_masuk,
            jenis_kelamin: editForm.jenis_kelamin,
            user_id: editUser.id,
          });
        }
      }

      toast.success(`Akun ${editForm.name} berhasil diperbarui!`);
      setEditUser(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal memperbarui akun');
    } finally { setEditLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus akun pengguna ini?')) return;
    await axios.delete(`${API}/users/${id}`);
    toast.success('Akun dihapus'); fetchAll();
  };

  const handleToggleActive = async (u) => {
    await axios.put(`${API}/users/${u.id}`, { is_active: !u.is_active });
    toast.success(u.is_active ? 'Akun dinonaktifkan' : 'Akun diaktifkan');
    fetchAll();
  };

  const filtered = users.filter(u =>
    (filter === 'all' || u.role === filter) &&
    (!searchName || u.name.toLowerCase().includes(searchName.toLowerCase()))
  );

  const roleColors = {
    admin: 'bg-red-100 text-red-700',
    guru: 'bg-emerald-100 text-emerald-700',
    santri: 'bg-purple-100 text-purple-700',
  };

  const modalBaseClass = "fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4";
  const modalPanelClass = "bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto";

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6 flex items-start md:items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-emerald-950">Manajemen Akun</h1>
            <p className="text-sm text-gray-500">Kelola semua akun pengguna ZahBaq</p>
          </div>
          <button
            data-testid="add-akun-btn"
            onClick={() => { setForm(emptyForm); setShowForm(true); }}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-emerald transition-all"
          >
            <Plus size={16} /> Buat Akun Baru
          </button>
        </div>

        <div className="p-4 md:p-8">
          {/* Search by name */}
          <div className="relative mb-4">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="search-akun-name"
              type="text"
              placeholder="Cari berdasarkan nama..."
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
              className="w-full sm:max-w-xs pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-white"
            />
            {searchName && (
              <button onClick={() => setSearchName('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded-full">
                <X size={13} className="text-gray-400" />
              </button>
            )}
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mb-5">
            {[['all', 'Semua'], ['guru', 'Guru'], ['santri', 'Santri']].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`px-4 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  filter === k ? 'bg-emerald-500 text-white shadow-emerald' : 'bg-white text-gray-600 border border-gray-200 hover:bg-emerald-50'
                }`}
              >
                {l} {k !== 'all' ? `(${users.filter(u => u.role === k).length})` : `(${users.length})`}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-card overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-emerald-50">
                  {['Username', 'Nama', 'Role', 'WhatsApp', 'Status', 'Aksi'].map(h => (
                    <th key={h} className="px-5 py-4 text-left text-xs font-semibold text-emerald-700 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((u, i) => (
                  <motion.tr
                    key={u.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-5 py-3.5 text-sm font-mono text-gray-600">{u.username}</td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-gray-800">{u.name}</td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-xl capitalize ${roleColors[u.role] || 'bg-gray-100 text-gray-600'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-500">{u.whatsapp || '-'}</td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => handleToggleActive(u)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-xl transition-colors ${
                          u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {u.is_active !== false ? 'Aktif' : 'Nonaktif'}
                      </button>
                    </td>
                    <td className="px-5 py-3.5">
                      {u.role !== 'admin' && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            data-testid={`edit-akun-btn-${u.id}`}
                            onClick={() => openEdit(u)}
                            className="text-xs text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                          >
                            <Edit2 size={12} /> Edit
                          </button>
                          <button
                            data-testid={`lihat-password-btn-${u.id}`}
                            onClick={() => handleViewPassword(u)}
                            className="text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                          >
                            <KeyRound size={12} /> Lihat
                          </button>
                          <button
                            data-testid={`reset-password-btn-${u.id}`}
                            onClick={() => { setResetPassUser(u); setResetPassInput(''); setShowResetPassText(false); }}
                            className="text-xs text-amber-500 hover:text-amber-700 hover:bg-amber-50 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                          >
                            <RefreshCw size={12} /> Reset
                          </button>
                          <button
                            data-testid={`delete-akun-btn-${u.id}`}
                            onClick={() => handleDelete(u.id)}
                            className="text-xs text-red-400 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                          >
                            Hapus
                          </button>
                        </div>
                      )}
                    </td>
                  </motion.tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-gray-400 text-sm">Belum ada pengguna</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>

        {/* ── Create Account Modal ── */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className={modalBaseClass}
              onClick={e => e.target === e.currentTarget && setShowForm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
                className={modalPanelClass}
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="font-playfair text-xl font-bold text-emerald-950">Buat Akun Baru</h2>
                  <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                    <X size={18} />
                  </button>
                </div>
                <div className="p-6">
                  {/* Role selector */}
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Tipe Akun</label>
                    <div className="grid grid-cols-2 gap-2">
                      {ROLES.map(({ key, label, icon: Icon }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setRole(key)}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-semibold transition-all ${
                            role === key
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <Icon size={18} />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Username *</label>
                        <input
                          required
                          data-testid="create-username"
                          value={form.username}
                          onChange={e => setFormField('username', e.target.value)}
                          placeholder="username.unik"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Lengkap *</label>
                        <input
                          required
                          data-testid="create-name"
                          value={form.name}
                          onChange={e => setFormField('name', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password *</label>
                      <div className="relative">
                        <input
                          required
                          data-testid="create-password"
                          type={showPass ? 'text' : 'password'}
                          value={form.password}
                          onChange={e => setFormField('password', e.target.value)}
                          className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                        <button type="button" onClick={() => setShowPass(s => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">WhatsApp (62xxx)</label>
                      <input
                        data-testid="create-whatsapp"
                        value={form.whatsapp}
                        onChange={e => setFormField('whatsapp', e.target.value)}
                        placeholder="6281234567890"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>

                    <RoleFields f={form} onChange={setFormField} currentRole={role} halaqah={halaqah} />

                    {role === 'santri' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                        Akun santri akan otomatis dibuat sekaligus dengan data profil santri (NIS diperlukan).
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600"
                      >
                        Batal
                      </button>
                      <button
                        data-testid="akun-form-submit"
                        type="submit"
                        disabled={loading}
                        className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold shadow-emerald flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {loading
                          ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <><Check size={15} /> Buat Akun</>
                        }
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Edit Account Modal ── */}
        <AnimatePresence>
          {editUser && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className={modalBaseClass}
              onClick={e => e.target === e.currentTarget && setEditUser(null)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
                className={modalPanelClass}
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div>
                    <h2 className="font-playfair text-xl font-bold text-emerald-950">Edit Akun</h2>
                    <p className="text-xs text-gray-400 mt-0.5">@{editUser.username} · {editUser.role}</p>
                  </div>
                  <button onClick={() => setEditUser(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                    <X size={18} />
                  </button>
                </div>
                <div className="p-6">
                  <form onSubmit={handleEditSubmit} data-testid="edit-akun-form" className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Username *</label>
                      <input
                        required
                        data-testid="edit-username"
                        value={editForm.username}
                        onChange={e => setEditFormField('username', e.target.value)}
                        placeholder="username.unik"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Lengkap *</label>
                      <input
                        required
                        data-testid="edit-name"
                        value={editForm.name}
                        onChange={e => setEditFormField('name', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Password Baru <span className="text-gray-400 font-normal">(kosongkan jika tidak diubah)</span>
                      </label>
                      <div className="relative">
                        <input
                          data-testid="edit-password"
                          type={showEditPass ? 'text' : 'password'}
                          value={editForm.password}
                          onChange={e => setEditFormField('password', e.target.value)}
                          placeholder="Isi untuk mengubah password"
                          className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                        <button type="button" onClick={() => setShowEditPass(s => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showEditPass ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">WhatsApp (62xxx)</label>
                      <input
                        data-testid="edit-whatsapp"
                        value={editForm.whatsapp}
                        onChange={e => setEditFormField('whatsapp', e.target.value)}
                        placeholder="6281234567890"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>

                    <RoleFields f={editForm} onChange={setEditFormField} currentRole={editUser.role} halaqah={halaqah} />

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setEditUser(null)}
                        className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600"
                      >
                        Batal
                      </button>
                      <button
                        data-testid="edit-akun-submit"
                        type="submit"
                        disabled={editLoading}
                        className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold shadow-emerald flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {editLoading
                          ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <><Check size={15} /> Simpan Perubahan</>
                        }
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Lihat Password Modal ── */}
        <AnimatePresence>
          {viewPassUser && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className={modalBaseClass}
              onClick={e => e.target === e.currentTarget && setViewPassUser(null)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-sm"
              >
                <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                  <div>
                    <h2 className="font-semibold text-gray-800">Password Akun</h2>
                    <p className="text-xs text-gray-400 mt-0.5">@{viewPassUser.username}</p>
                  </div>
                  <button onClick={() => setViewPassUser(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                    <X size={16} />
                  </button>
                </div>
                <div className="p-6">
                  {viewPassLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : viewPassData === null ? (
                    <p className="text-sm text-red-500 text-center">Password tidak tersedia untuk akun lama</p>
                  ) : (
                    <div className="space-y-3">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Password</label>
                      <div className="relative">
                        <div
                          data-testid="view-password-value"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-mono text-gray-800 pr-12 select-all"
                        >
                          {showViewPassText ? viewPassData : '••••••••'}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowViewPassText(s => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showViewPassText ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setViewPassUser(null)}
                    className="mt-4 w-full py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50"
                  >
                    Tutup
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Reset Password Modal ── */}
        <AnimatePresence>
          {resetPassUser && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className={modalBaseClass}
              onClick={e => e.target === e.currentTarget && setResetPassUser(null)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-sm"
              >
                <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                  <div>
                    <h2 className="font-semibold text-gray-800">Reset Password</h2>
                    <p className="text-xs text-gray-400 mt-0.5">@{resetPassUser.username} · {resetPassUser.name}</p>
                  </div>
                  <button onClick={() => setResetPassUser(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                    <X size={16} />
                  </button>
                </div>
                <div className="p-6">
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password Baru *</label>
                      <div className="relative">
                        <input
                          required
                          data-testid="reset-password-input"
                          type={showResetPassText ? 'text' : 'password'}
                          value={resetPassInput}
                          onChange={e => setResetPassInput(e.target.value)}
                          placeholder="Ketik password baru"
                          className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetPassText(s => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showResetPassText ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => setResetPassUser(null)}
                        className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600"
                      >
                        Batal
                      </button>
                      <button
                        data-testid="reset-password-submit"
                        type="submit"
                        disabled={resetPassLoading}
                        className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {resetPassLoading
                          ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <><Check size={15} /> Simpan</>
                        }
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
